import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Save, Loader2, Scale, User, Calendar, Building2, AlertCircle, Check, ChevronRight, ChevronLeft, FileText
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

const CASE_TYPE_OPTIONS = ['Dissolution', 'DVRO', 'Child Custody', 'Spousal Support', 'Property'];
const STATUS_OPTIONS = ['Active', 'Pending', 'Closed', 'On Hold'];
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];

// Helper function to calculate duration between two dates
const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return { years: '', months: '' };
  
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
      return { years: '', months: '' };
    }
    
    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    
    if (months < 0) {
      years--;
      months += 12;
    }
    
    return { years: years.toString(), months: months.toString() };
  } catch (error) {
    return { years: '', months: '' };
  }
};

export default function CaseFormPage() {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 6;
  
  // Related entities
  const [clients, setClients] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [opposingCounsels, setOpposingCounsels] = useState([]);
  const [counties, setCounties] = useState([]);
  const [courts, setCourts] = useState([]);
  
  const [caseForm, setCaseForm] = useState({
    court_case_number: '',
    case_title: '',
    case_types: [],
    status: 'Active',
    description: '',
    client_id: '',
    attorney_id: '',
    opposing_counsel_id: '',
    court_id: '',
    opposing_party_name: '',
    opposing_party_address_line_1: '',
    opposing_party_address_line_2: '',
    opposing_party_city: '',
    opposing_party_state: '',
    opposing_party_zip: '',
    petitioner_name: '',
    respondent_name: '',
    petitioner_party: 'client',
    filing_date: '',
    next_hearing_date: '',
    trial_date: '',
    date_of_marriage: '',
    date_of_separation: '',
    judge_name: '',
    priority: 'Medium',
    // DVRO/TRO Information
    protected_person_name: '',
    protected_person_street: '',
    protected_person_city: '',
    protected_person_state: '',
    protected_person_zip: '',
    protected_person_phone: '',
    restrained_person_name: '',
    restrained_person_sex: '',
    restrained_person_height: '',
    restrained_person_weight: '',
    restrained_person_race: '',
    restrained_person_hair_color: '',
    restrained_person_eye_color: '',
    restrained_person_age: '',
    restrained_person_dob: ''
  });

  useEffect(() => {
    const initData = async () => {
      await fetchUser();
      await Promise.all([
        fetchClients(),
        fetchAttorneys(),
        fetchOpposingCounsels(),
        fetchCourts(),
        fetchOptionLists()
      ]);
      
      if (caseId) {
        await fetchCase();
      }
      
      setLoading(false);
    };
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Auto-generate case title when petitioner/respondent change
  useEffect(() => {
    if (caseForm.petitioner_name && caseForm.respondent_name) {
      const title = `${caseForm.petitioner_name} v ${caseForm.respondent_name}`;
      setCaseForm(prev => ({ ...prev, case_title: title }));
    }
  }, [caseForm.petitioner_name, caseForm.respondent_name]);

  // Auto-set petitioner/respondent names based on party selection
  useEffect(() => {
    const client = clients.find(c => c.client_id === caseForm.client_id);
    const clientName = client?.full_name || '';
    
    if (caseForm.petitioner_party === 'client') {
      setCaseForm(prev => ({
        ...prev,
        petitioner_name: clientName,
        respondent_name: prev.opposing_party_name
      }));
    } else {
      setCaseForm(prev => ({
        ...prev,
        petitioner_name: prev.opposing_party_name,
        respondent_name: clientName
      }));
    }
  }, [caseForm.petitioner_party, caseForm.client_id, caseForm.opposing_party_name, clients]);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        if (userData.app_mode !== 'crm') {
          toast.error('CRM features require CRM subscription');
          navigate('/dashboard');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchClients = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchAttorneys = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/firm`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const attorneysList = data.attorneys || [];
        setAttorneys(attorneysList);
        
        // Auto-select default attorney if not editing
        if (!caseId && attorneysList.length > 0) {
          const defaultAttorney = attorneysList.find(a => a.is_default) || attorneysList[0];
          setCaseForm(prev => ({ ...prev, attorney_id: defaultAttorney.attorney_id }));
        }
      }
    } catch (error) {
      console.error('Error fetching attorneys:', error);
    }
  };

  const fetchCourts = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/courts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCourts(data);
      }
    } catch (error) {
      console.error('Error fetching courts:', error);
    }
  };

  const fetchOpposingCounsels = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/opposing-counsel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOpposingCounsels(data);
      }
    } catch (error) {
      console.error('Error fetching opposing counsels:', error);
    }
  };

  const fetchOptionLists = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/global-fields/option-lists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const systemLists = data.system_lists || [];
        
        const countyList = systemLists.find(l => l.name === 'CaliforniaCounty');
        if (countyList) setCounties(countyList.options);
      }
    } catch (error) {
      console.error('Error fetching option lists:', error);
    }
  };

  const fetchCase = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/cases/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCaseForm({
          court_case_number: data.court_case_number || '',
          case_title: data.case_title || '',
          case_types: data.case_types || [],
          status: data.status || 'Active',
          description: data.description || '',
          client_id: data.client_id || '',
          attorney_id: data.attorney_id || '',
          opposing_counsel_id: data.opposing_counsel_id || '',
          court_id: data.court_id || '',
          opposing_party_name: data.opposing_party_name || '',
          opposing_party_address_line_1: data.opposing_party_address_line_1 || '',
          opposing_party_address_line_2: data.opposing_party_address_line_2 || '',
          opposing_party_city: data.opposing_party_city || '',
          opposing_party_state: data.opposing_party_state || '',
          opposing_party_zip: data.opposing_party_zip || '',
          petitioner_name: data.petitioner_name || '',
          respondent_name: data.respondent_name || '',
          petitioner_party: data.petitioner_party || 'client',
          filing_date: data.filing_date || '',
          next_hearing_date: data.next_hearing_date || '',
          trial_date: data.trial_date || '',
          date_of_marriage: data.date_of_marriage || '',
          date_of_separation: data.date_of_separation || '',
          judge_name: data.judge_name || '',
          priority: data.priority || 'Medium',
          protected_person_name: data.protected_person_name || '',
          protected_person_street: data.protected_person_street || '',
          protected_person_city: data.protected_person_city || '',
          protected_person_state: data.protected_person_state || '',
          protected_person_zip: data.protected_person_zip || '',
          protected_person_phone: data.protected_person_phone || '',
          restrained_person_name: data.restrained_person_name || '',
          restrained_person_sex: data.restrained_person_sex || '',
          restrained_person_height: data.restrained_person_height || '',
          restrained_person_weight: data.restrained_person_weight || '',
          restrained_person_race: data.restrained_person_race || '',
          restrained_person_hair_color: data.restrained_person_hair_color || '',
          restrained_person_eye_color: data.restrained_person_eye_color || '',
          restrained_person_age: data.restrained_person_age || '',
          restrained_person_dob: data.restrained_person_dob || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load case');
      navigate('/cases');
    }
  };

  const handleSaveCase = async () => {
    // Final validation before save
    if (!validateStep(1) || !validateStep(2)) {
      setCurrentStep(1);
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const url = caseId 
        ? `${API}/crm/cases/${caseId}`
        : `${API}/crm/cases`;
      
      const response = await fetch(url, {
        method: caseId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(caseForm)
      });
      
      if (response.ok) {
        toast.success(caseId ? 'Case updated' : 'Case created');
        navigate('/cases');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save case');
      }
    } catch (error) {
      toast.error('Failed to save case');
    } finally {
      setSaving(false);
    }
  };

  // Wizard navigation
  const validateStep = (step) => {
    switch(step) {
      case 1: // Case Details
        if (caseForm.case_types.length === 0) {
          toast.error('Select at least one case type');
          return false;
        }
        return true;
      case 2: // Parties
        if (!caseForm.client_id) {
          toast.error('Please select a client');
          return false;
        }
        if (!caseForm.opposing_party_name.trim()) {
          toast.error('Opposing party name is required');
          return false;
        }
        if (!caseForm.petitioner_name.trim() || !caseForm.respondent_name.trim()) {
          toast.error('Both petitioner and respondent names are required');
          return false;
        }
        return true;
      default:
        return true; // Steps 3-6 have no required fields
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleStepClick = (step) => {
    // Allow jumping to any previous step or next step if current is valid
    if (step < currentStep || (step === currentStep + 1 && validateStep(currentStep))) {
      setCurrentStep(step);
    } else if (step === currentStep) {
      return;
    } else {
      toast.error('Please complete the current step first');
    }
  };

  const steps = [
    { number: 1, title: 'Case Details', icon: Scale },
    { number: 2, title: 'Parties', icon: User },
    { number: 3, title: 'Marriage Info', icon: Calendar },
    { number: 4, title: 'Important Dates', icon: Calendar },
    { number: 5, title: 'Court & Protection', icon: Building2 },
    { number: 6, title: 'Finalize', icon: FileText }
  ];


  const toggleCaseType = (type) => {
    if (caseForm.case_types.includes(type)) {
      setCaseForm({
        ...caseForm,
        case_types: caseForm.case_types.filter(t => t !== type)
      });
    } else {
      setCaseForm({
        ...caseForm,
        case_types: [...caseForm.case_types, type]
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-6 py-8 lg:pt-8 pt-20">
        
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/cases')}
            className="rounded-xl mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Cases
          </Button>
          <h1 className="text-3xl font-bold text-slate-900">
            {caseId ? 'Edit Case' : 'Add New Case'}
          </h1>
          <p className="text-slate-500 mt-1">Step {currentStep} of {totalSteps}: {steps[currentStep - 1].title}</p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              const isAccessible = step.number <= currentStep || (step.number === currentStep + 1 && validateStep(currentStep));
              
              return (
                <div key={step.number} className="flex items-center flex-1">
                  <button
                    onClick={() => handleStepClick(step.number)}
                    disabled={!isAccessible && step.number > currentStep}
                    className={`flex flex-col items-center gap-2 ${
                      isAccessible || isCompleted ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
                      isActive 
                        ? 'bg-violet-600 border-violet-600 text-white shadow-lg scale-110' 
                        : isCompleted
                        ? 'bg-green-100 border-green-500 text-green-700'
                        : 'bg-white border-slate-300 text-slate-400'
                    }`}>
                      {isCompleted ? (
                        <Check className="w-6 h-6" />
                      ) : (
                        <StepIcon className="w-6 h-6" />
                      )}
                    </div>
                    <span className={`text-xs font-medium ${
                      isActive ? 'text-violet-600' : isCompleted ? 'text-slate-700' : 'text-slate-400'
                    }`}>
                      {step.title}
                    </span>
                  </button>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${
                      isCompleted ? 'bg-green-500' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Step 1: Case Details */}
          {currentStep === 1 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-violet-600" />
              Case Identification
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Court Case Number (Optional)</Label>
                  <Input
                    value={caseForm.court_case_number}
                    onChange={(e) => setCaseForm({ ...caseForm, court_case_number: e.target.value })}
                    placeholder="FL-2024-001234"
                    className="mt-1"
                    data-testid="court-case-number-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave blank if not yet assigned</p>
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Case Title (Auto-generated)</Label>
                  <Input
                    value={caseForm.case_title}
                    readOnly
                    className="mt-1 bg-slate-50"
                    placeholder="Will auto-populate"
                  />
                  <p className="text-xs text-slate-500 mt-1">Based on Petitioner v Respondent</p>
                </div>
              </div>
              
              <div>
                <Label className="text-sm text-slate-600">Case Type(s) * (Select all that apply)</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {CASE_TYPE_OPTIONS.map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleCaseType(type)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        caseForm.case_types.includes(type)
                          ? 'bg-violet-600 text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`case-type-${type.toLowerCase().replace(' ', '-')}`}
                    >
                      {caseForm.case_types.includes(type) && '✓ '}
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Status</Label>
                  <select
                    value={caseForm.status}
                    onChange={(e) => setCaseForm({ ...caseForm, status: e.target.value })}
                    className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Priority</Label>
                  <select
                    value={caseForm.priority}
                    onChange={(e) => setCaseForm({ ...caseForm, priority: e.target.value })}
                    className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                  >
                    {PRIORITY_OPTIONS.map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Step 2: Parties */}
          {currentStep === 2 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-violet-600" />
              Parties
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Client *</Label>
                <select
                  value={caseForm.client_id}
                  onChange={(e) => setCaseForm({ ...caseForm, client_id: e.target.value })}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                  data-testid="client-select"
                >
                  <option value="">Select Client</option>
                  {clients.map(client => (
                    <option key={client.client_id} value={client.client_id}>
                      {client.full_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-sm text-slate-600">Assigned Attorney (Optional - Leave blank for Pro Se)</Label>
                <select
                  value={caseForm.attorney_id}
                  onChange={(e) => setCaseForm({ ...caseForm, attorney_id: e.target.value })}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                  data-testid="attorney-select"
                >
                  <option value="">Pro Se (Self-Represented)</option>
                  {attorneys.map(attorney => (
                    <option key={attorney.attorney_id} value={attorney.attorney_id}>
                      {attorney.full_name} {attorney.is_default && '(Default)'}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label className="text-sm text-slate-600">Opposing Party Name *</Label>
                <Input
                  value={caseForm.opposing_party_name}
                  onChange={(e) => setCaseForm({ ...caseForm, opposing_party_name: e.target.value })}
                  placeholder="Jane Doe"
                  className="mt-1"
                  data-testid="opposing-party-input"
                />
              </div>
              

              {/* Opposing Party Address */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-slate-700 font-semibold">Opposing Party Address</Label>
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-blue-600 hover:text-blue-700">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          const selectedClient = clients.find(c => c.client_id === caseForm.client_id);
                          if (selectedClient && selectedClient.address) {
                            setCaseForm(prev => ({
                              ...prev,
                              opposing_party_address_line_1: selectedClient.address.address_line_1 || '',
                              opposing_party_address_line_2: selectedClient.address.address_line_2 || '',
                              opposing_party_city: selectedClient.address.city || '',
                              opposing_party_state: selectedClient.address.state || '',
                              opposing_party_zip: selectedClient.address.zip || ''
                            }));
                            toast.success('Address copied from client');
                          } else {
                            toast.error('Please select a client first');
                            e.target.checked = false;
                          }
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span>Same as client</span>
                  </label>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600">Address Line 1</Label>
                    <Input
                      value={caseForm.opposing_party_address_line_1}
                      onChange={(e) => setCaseForm({ ...caseForm, opposing_party_address_line_1: e.target.value })}
                      placeholder="123 Main Street"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Address Line 2</Label>
                    <Input
                      value={caseForm.opposing_party_address_line_2}
                      onChange={(e) => setCaseForm({ ...caseForm, opposing_party_address_line_2: e.target.value })}
                      placeholder="Apt 4B"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div>
                    <Label className="text-sm text-slate-600">City</Label>
                    <Input
                      value={caseForm.opposing_party_city}
                      onChange={(e) => setCaseForm({ ...caseForm, opposing_party_city: e.target.value })}
                      placeholder="Los Angeles"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">State</Label>
                    <Input
                      value={caseForm.opposing_party_state}
                      onChange={(e) => setCaseForm({ ...caseForm, opposing_party_state: e.target.value })}
                      placeholder="CA"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">ZIP</Label>
                    <Input
                      value={caseForm.opposing_party_zip}
                      onChange={(e) => setCaseForm({ ...caseForm, opposing_party_zip: e.target.value })}
                      placeholder="90001"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm text-slate-600">Opposing Counsel (Optional - Leave blank for Pro Se)</Label>
                <select
                  value={caseForm.opposing_counsel_id}
                  onChange={(e) => setCaseForm({ ...caseForm, opposing_counsel_id: e.target.value })}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                >
                  <option value="">Pro Se (Self-Represented)</option>
                  {opposingCounsels.map(counsel => (
                    <option key={counsel.counsel_id} value={counsel.counsel_id}>
                      {counsel.first_name} {counsel.last_name} - {counsel.law_firm || 'Independent'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Petitioner/Respondent */}
              <div className="p-4 rounded-xl bg-violet-50 border border-violet-200">
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Who is the Petitioner? *</Label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="petitioner_party"
                      value="client"
                      checked={caseForm.petitioner_party === 'client'}
                      onChange={(e) => setCaseForm({ ...caseForm, petitioner_party: e.target.value })}
                      className="w-4 h-4 text-violet-600"
                    />
                    <span className="text-sm font-medium text-slate-700">Your Client</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="petitioner_party"
                      value="opposing"
                      checked={caseForm.petitioner_party === 'opposing'}
                      onChange={(e) => setCaseForm({ ...caseForm, petitioner_party: e.target.value })}
                      className="w-4 h-4 text-violet-600"
                    />
                    <span className="text-sm font-medium text-slate-700">Opposing Party</span>
                  </label>
                </div>
                <div className="mt-3 p-3 rounded-lg bg-white border border-violet-200">
                  <p className="text-xs text-slate-600">
                    <strong>Petitioner:</strong> {caseForm.petitioner_name || '(Not set)'}<br/>
                    <strong>Respondent:</strong> {caseForm.respondent_name || '(Not set)'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Step 3: Marriage Information */}
          {currentStep === 3 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-600" />
              Marriage Information
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600">Date of Marriage</Label>
                  <Input
                    type="date"
                    value={caseForm.date_of_marriage}
                    onChange={(e) => setCaseForm({ ...caseForm, date_of_marriage: e.target.value })}
                    className="mt-1"
                    data-testid="date-of-marriage-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-600">Date of Separation</Label>
                  <Input
                    type="date"
                    value={caseForm.date_of_separation}
                    onChange={(e) => setCaseForm({ ...caseForm, date_of_separation: e.target.value })}
                    className="mt-1"
                    data-testid="date-of-separation-input"
                  />
                </div>
              </div>
              
              {/* Duration of Marriage (Read-only, Calculated) */}
              {(() => {
                const duration = calculateDuration(caseForm.date_of_marriage, caseForm.date_of_separation);
                const durationText = duration.years || duration.months 
                  ? `${duration.years || '0'} Years & ${duration.months || '0'} Months`
                  : '';
                
                return durationText && (
                  <div>
                    <Label className="text-sm text-slate-600">Duration of Marriage</Label>
                    <Input
                      value={durationText}
                      readOnly
                      className="mt-1 bg-slate-50 text-slate-700 font-medium"
                      placeholder="Will calculate automatically"
                      data-testid="marriage-duration-display"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Automatically calculated from dates above
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
          )}

          {/* Step 4: Important Dates */}
          {currentStep === 4 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-violet-600" />
              Important Dates
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-sm text-slate-600">Filing Date</Label>
                <Input
                  type="date"
                  value={caseForm.filing_date}
                  onChange={(e) => setCaseForm({ ...caseForm, filing_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Next Hearing</Label>
                <Input
                  type="date"
                  value={caseForm.next_hearing_date}
                  onChange={(e) => setCaseForm({ ...caseForm, next_hearing_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Trial Date</Label>
                <Input
                  type="date"
                  value={caseForm.trial_date}
                  onChange={(e) => setCaseForm({ ...caseForm, trial_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          )}

          {/* Step 5: Court & Protection */}
          {currentStep === 5 && (
          <>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-violet-600" />
              Court Information
            </h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600">Court *</Label>
                <select
                  value={caseForm.court_id}
                  onChange={(e) => setCaseForm({ ...caseForm, court_id: e.target.value })}
                  className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white"
                  required
                >
                  <option value="">Select Court</option>
                  {courts.map(court => (
                    <option key={court.court_id} value={court.court_id}>
                      {court.court_name} - {court.county} County
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Don't see your court?{' '}
                  <button 
                    type="button"
                    onClick={() => navigate('/courts')} 
                    className="text-violet-600 hover:underline"
                  >
                    Add it here
                  </button>
                </p>
              </div>

              <div>
                <Label className="text-sm text-slate-600">Judge Name</Label>
                <Input
                  value={caseForm.judge_name}
                  onChange={(e) => setCaseForm({ ...caseForm, judge_name: e.target.value })}
                  placeholder="Hon. John Smith"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* DVRO/TRO Information */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-violet-600" />
              DVRO/TRO Information
            </h2>
            
            <div className="grid grid-cols-2 gap-6">
              {/* Protected Person */}
              <div>
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
                  <h3 className="text-sm font-semibold text-slate-700">Protected Person</h3>
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-blue-600 hover:text-blue-700">
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          const selectedClient = clients.find(c => c.client_id === caseForm.client_id);
                          if (selectedClient) {
                            setCaseForm(prev => ({
                              ...prev,
                              protected_person_name: selectedClient.full_name || '',
                              protected_person_street: selectedClient.address?.address_line_1 || '',
                              protected_person_city: selectedClient.address?.city || '',
                              protected_person_state: selectedClient.address?.state || '',
                              protected_person_zip: selectedClient.address?.zip || '',
                              protected_person_phone: selectedClient.phone || ''
                            }));
                            toast.success('Populated from client profile');
                          } else {
                            toast.error('Please select a client first');
                            e.target.checked = false;
                          }
                        }
                      }}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span>Use Client Info</span>
                  </label>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-600">Name</Label>
                    <Input
                      value={caseForm.protected_person_name}
                      onChange={(e) => setCaseForm({ ...caseForm, protected_person_name: e.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Street</Label>
                    <Input
                      value={caseForm.protected_person_street}
                      onChange={(e) => setCaseForm({ ...caseForm, protected_person_street: e.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-slate-600">City</Label>
                      <Input
                        value={caseForm.protected_person_city}
                        onChange={(e) => setCaseForm({ ...caseForm, protected_person_city: e.target.value })}
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">State</Label>
                      <Input
                        value={caseForm.protected_person_state}
                        onChange={(e) => setCaseForm({ ...caseForm, protected_person_state: e.target.value })}
                        placeholder="CA"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Zip</Label>
                      <Input
                        value={caseForm.protected_person_zip}
                        onChange={(e) => setCaseForm({ ...caseForm, protected_person_zip: e.target.value })}
                        className="mt-1 h-9"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Phone (optional)</Label>
                    <Input
                      value={caseForm.protected_person_phone}
                      onChange={(e) => setCaseForm({ ...caseForm, protected_person_phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="mt-1 h-9"
                    />
                  </div>
                </div>
              </div>

              {/* Restrained Person */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-200">Restrained Person</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-600">Name</Label>
                    <Input
                      value={caseForm.restrained_person_name}
                      onChange={(e) => setCaseForm({ ...caseForm, restrained_person_name: e.target.value })}
                      className="mt-1 h-9"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-600">Sex</Label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="restrained_person_sex"
                          value="M"
                          checked={caseForm.restrained_person_sex === 'M'}
                          onChange={(e) => setCaseForm({ ...caseForm, restrained_person_sex: e.target.value })}
                          className="w-4 h-4 text-violet-600"
                        />
                        <span className="text-sm text-slate-700">M</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="restrained_person_sex"
                          value="F"
                          checked={caseForm.restrained_person_sex === 'F'}
                          onChange={(e) => setCaseForm({ ...caseForm, restrained_person_sex: e.target.value })}
                          className="w-4 h-4 text-violet-600"
                        />
                        <span className="text-sm text-slate-700">F</span>
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs text-slate-600">Height</Label>
                      <Input
                        value={caseForm.restrained_person_height}
                        onChange={(e) => setCaseForm({ ...caseForm, restrained_person_height: e.target.value })}
                        placeholder="5'10&quot;"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Weight</Label>
                      <Input
                        value={caseForm.restrained_person_weight}
                        onChange={(e) => setCaseForm({ ...caseForm, restrained_person_weight: e.target.value })}
                        placeholder="180"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Race</Label>
                      <Input
                        value={caseForm.restrained_person_race}
                        onChange={(e) => setCaseForm({ ...caseForm, restrained_person_race: e.target.value })}
                        className="mt-1 h-9"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-600">Hair</Label>
                      <Input
                        value={caseForm.restrained_person_hair_color}
                        onChange={(e) => setCaseForm({ ...caseForm, restrained_person_hair_color: e.target.value })}
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Eyes</Label>
                      <Input
                        value={caseForm.restrained_person_eye_color}
                        onChange={(e) => setCaseForm({ ...caseForm, restrained_person_eye_color: e.target.value })}
                        className="mt-1 h-9"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-slate-600">Date of Birth</Label>
                      <Input
                        type="date"
                        value={caseForm.restrained_person_dob}
                        onChange={(e) => {
                          const newDob = e.target.value;
                          setCaseForm({ ...caseForm, restrained_person_dob: newDob });
                          
                          // Auto-calculate age
                          if (newDob) {
                            try {
                              const birthDate = new Date(newDob);
                              const today = new Date();
                              let age = today.getFullYear() - birthDate.getFullYear();
                              const monthDiff = today.getMonth() - birthDate.getMonth();
                              if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                                age--;
                              }
                              setCaseForm(prev => ({ ...prev, restrained_person_age: age.toString() }));
                            } catch (error) {
                              // Invalid date, skip calculation
                            }
                          }
                        }}
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Age</Label>
                      <Input
                        value={caseForm.restrained_person_age}
                        onChange={(e) => setCaseForm({ ...caseForm, restrained_person_age: e.target.value })}
                        placeholder="35"
                        className="mt-1 h-9"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </>
          )}

          {/* Step 6: Finalize */}
          {currentStep === 6 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Case Description / Notes</h2>
            <Textarea
              value={caseForm.description}
              onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })}
              placeholder="Enter case details, notes, or important information..."
              className="min-h-[120px]"
            />
          </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between py-6">
            {currentStep > 1 ? (
              <Button
                variant="outline"
                onClick={handlePrevious}
                className="rounded-xl"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => navigate('/cases')}
                className="rounded-xl"
              >
                Cancel
              </Button>
            )}
            
            {currentStep < totalSteps ? (
              <Button
                onClick={handleNext}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white px-8"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSaveCase}
                disabled={saving}
                className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white px-8"
                data-testid="save-case-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> {caseId ? 'Update Case' : 'Create Case'}</>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    </div></div>
  );
}
